<script setup>
/* global axios */
// Russian-only module, same localization boundary as Telegram settings.
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ReviewMedia from './ReviewMedia.vue';
import {
  wordListError,
  tabs,
  labels,
  parseOwnedIds,
  errorMessage,
  effectiveGroupRule,
  commonGroupRule,
  applyGroupEdits,
  defaultNoticeText,
  noticeTextError,
} from './model';

const route = useRoute();
const tab = ref('rules');
const settings = ref(null);
const rows = ref([]);
const cursor = ref(null);
const loading = ref(false);
const saving = ref(false);
const error = ref('');
const notice = ref('');
const wordErrors = ref({});
const denied = ref(false);
const uncertain = ref(false);
const owned = ref('');
const dialog = ref(null);
const selected = ref(null);
const note = ref('');
const availableGroups = ref([]);
const selectedGroup = ref('all');
const ruleEdits = ref({});
const mediaEdits = ref({});
// Preserve the literal text while editing; API payloads use normalized lists.
const wordDrafts = ref({});
const networkBaseline = ref(null);
const allGroups = computed(() => selectedGroup.value === 'all');
const selectedIds = computed(() =>
  availableGroups.value
    .filter(
      group => allGroups.value || String(group.id) === selectedGroup.value
    )
    .map(group => String(group.id))
);
const editedGroups = computed(() =>
  applyGroupEdits(
    settings.value?.settings?.groups,
    selectedIds.value,
    {
      ...(ruleEdits.value[selectedGroup.value] || {}),
      ...Object.fromEntries(
        Object.entries(wordDrafts.value[selectedGroup.value] || {}).map(
          ([key, value]) => [
            key,
            [
              ...new Set(
                value
                  .split('\n')
                  .map(item => item.trim())
                  .filter(Boolean)
              ),
            ],
          ]
        )
      ),
    },
    mediaEdits.value[selectedGroup.value] || {}
  )
);
const selectedRules = computed(() =>
  selectedIds.value.map(id => effectiveGroupRule(editedGroups.value[id]))
);
const groupRule = computed(() => commonGroupRule(selectedRules.value));
const networkRestriction = ref(false);
const material = ref(null);
let materialEpoch = 0;
function changeRule(key, value) {
  notice.value = '';
  ruleEdits.value[selectedGroup.value] ||= {};
  ruleEdits.value[selectedGroup.value][key] = value;
}
function mixed(key) {
  return groupRule.value?.[key] === undefined;
}
function resetDrafts(data) {
  ruleEdits.value = {};
  mediaEdits.value = {};
  wordDrafts.value = {};
  wordErrors.value = {};
  networkBaseline.value = {
    mode: data.mode,
    topic: data.topic,
    owned_ids: [...data.owned_ids],
  };
}
function wordsValue(key) {
  return (
    wordDrafts.value[selectedGroup.value]?.[key] ??
    (groupRule.value?.[key] || []).join('\n')
  );
}
function wordsChanged(key, value) {
  notice.value = '';
  wordErrors.value[key] = '';
  if (!uncertain.value) error.value = '';
  wordDrafts.value[selectedGroup.value] ||= {};
  wordDrafts.value[selectedGroup.value][key] = value;
}
watch(
  [
    wordDrafts,
    ruleEdits,
    mediaEdits,
    owned,
    () => settings.value?.topic,
    () => settings.value?.mode,
  ],
  () => {
    notice.value = '';
  },
  { deep: true, flush: 'sync' }
);
let epoch = 0;
const emptyTitle = computed(
  () =>
    ({
      review: 'Нет сообщений на проверке',
      targets: 'Ограничений пока нет',
      decisions: 'Журнал пока пуст',
    })[tab.value]
);
const dialogDescription = computed(() => {
  if (selected.value?.verdict === 'ordinary')
    return 'Резерв освободится. Оплаченные размещения не будут списаны.';
  if (
    selected.value?.verdict === 'allow' &&
    selected.value?.row.placement_state === 'reserved'
  )
    return 'Объявление останется в группе. Один резерв станет одним использованным размещением.';
  if (selected.value?.kind === 'restore')
    return 'Запрет будет снят только в выбранной группе. Исключение действует до нового нарушения.';
  if (selected.value?.verdict === 'violation')
    return networkRestriction.value
      ? 'Подтверждаете мошенничество или систематический спам: удалить сообщение и ограничить автора во всех включённых группах. Срок — по настройкам исходной группы.'
      : 'В рабочем режиме применятся правила только исходной группы. В пробном режиме Telegram не изменяется.';
  return 'Сообщение останется без наказания.';
});
const activeTab = computed(() =>
  tabs.findIndex(item => item.key === tab.value)
);
const endpoint = computed(
  () => `/api/v1/accounts/${route.params.accountId}/tg_moderation`
);
const title = computed(() => {
  if (selected.value?.verdict === 'ordinary')
    return 'Обычное сообщение — без списания';
  if (selected.value?.kind === 'restore')
    return 'Разрешить писать в этой группе';
  if (selected.value?.verdict === 'allow') return 'Разрешить сообщение';
  return 'Подтвердить нарушение';
});
const mediaTypes = [
  ['photo', 'Фото'],
  ['video', 'Видео'],
  ['animation', 'GIF'],
  ['video_note', 'Видеосообщения'],
  ['voice', 'Голосовые сообщения'],
  ['audio', 'Аудиофайлы'],
  ['document', 'Документы'],
];
function mediaChoice(key) {
  const values = selectedRules.value.map(rule =>
    rule.blocked_media.includes(key)
  );
  return values.every(value => value === values[0]) ? values[0] : null;
}
function setMediaType(key, checked) {
  notice.value = '';
  mediaEdits.value[selectedGroup.value] ||= {};
  mediaEdits.value[selectedGroup.value][key] = checked;
}
const ruleRows = [
  [
    'money_spam',
    'Спам с денежными суммами',
    'Суммы без контекста и предложения постороннего заработка. Вопросы о ценах бьюти-услуг разрешены; неоднозначные сообщения — на проверку.',
  ],
  ['contacts', 'Контакты', 'Ссылки, @username, телефоны и карточки контактов'],
  [
    'media',
    'Вложения',
    'Фото, видео, GIF, голосовые, аудио и документы без права публикации',
  ],
  ['forwards', 'Пересылки', 'Пересланные сообщения без права публикации'],
  [
    'duplicates',
    'Повторы',
    'Нормализованный текст от 8 символов: один автор, одна группа, один час',
  ],
];
function formatDate(value) {
  return value ? new Date(value).toLocaleString('ru-RU') : '';
}
function label(value) {
  return labels[value] || 'Требуется проверка';
}
async function request(path, method = 'get', data = undefined) {
  return (
    await axios({ url: endpoint.value + path, method, data, timeout: 15000 })
  ).data;
}
const noticeScope = computed(() =>
  allGroups.value
    ? `Все группы (${selectedIds.value.length})`
    : availableGroups.value.find(
        group => String(group.id) === selectedGroup.value
      )?.title || ''
);
const noticeAvailable = ref(false);
const noticeConfigs = ref([]);
const noticeEdits = ref({});
const noticeSaving = ref(false);
const noticeUncertain = ref(false);
const noticeError = ref('');
const noticeSaved = ref('');
const selectedNotices = computed(() =>
  selectedIds.value.map(id => ({
    group_id: Number(id),
    enabled: false,
    text: defaultNoticeText,
    revision: 0,
    ...(noticeConfigs.value.find(item => String(item.group_id) === id) || {}),
    ...(noticeEdits.value[selectedGroup.value] || {}),
  }))
);
const noticeRule = computed(() =>
  Object.fromEntries(
    ['enabled', 'text'].map(key => {
      const values = selectedNotices.value.map(item => item[key]);
      if (!values.length)
        return [key, key === 'enabled' ? false : defaultNoticeText];
      const common = values.every(value => value === values[0]);
      return [key, common ? values[0] : undefined];
    })
  )
);
const noticeNeedsReview = computed(() =>
  noticeConfigs.value.some(
    item =>
      selectedIds.value.includes(String(item.group_id)) &&
      item.state === 'unknown'
  )
);
function changeNotice(key, value) {
  noticeEdits.value[selectedGroup.value] ||= {};
  noticeEdits.value[selectedGroup.value][key] = value;
  noticeSaved.value = '';
  noticeError.value = '';
}
async function saveNotice() {
  if (noticeSaving.value || noticeUncertain.value || !noticeAvailable.value)
    return;
  const invalid = selectedNotices.value
    .map(item => noticeTextError(item.text))
    .find(Boolean);
  if (invalid) {
    noticeError.value = invalid;
    return;
  }
  const current = epoch;
  const key = selectedGroup.value;
  const groups = selectedNotices.value.map(
    ({ group_id, enabled, text, revision }) => ({
      group_id,
      enabled,
      notice_text: text,
      revision,
    })
  );
  noticeSaving.value = true;
  noticeError.value = '';
  noticeSaved.value = '';
  try {
    const data = await request('/notices', 'patch', { groups });
    if (current !== epoch) return;
    noticeConfigs.value = data.items;
    delete noticeEdits.value[key];
    noticeSaved.value =
      'Настройки уведомления сохранены. Правила модерации не изменены.';
  } catch (failure) {
    if (current === epoch) {
      noticeUncertain.value = true;
      noticeError.value = `Сохранение не подтверждено. ${errorMessage(failure)} Обновите данные перед повтором.`;
    }
  } finally {
    if (current === epoch) noticeSaving.value = false;
  }
}

async function load(more = false) {
  materialEpoch += 1;
  material.value = null;
  epoch += 1;
  const current = epoch;
  loading.value = true;
  error.value = '';
  denied.value = false;
  if (!more) {
    rows.value = [];
    cursor.value = null;
  }
  try {
    const config = await request('/settings');
    if (current !== epoch) return;
    settings.value = config;
    noticeAvailable.value = config.notice_available === true;
    noticeConfigs.value = config.notice_settings || [];
    noticeEdits.value = {};
    noticeUncertain.value = false;
    noticeError.value = '';
    noticeSaved.value = '';
    availableGroups.value = config.groups || [];
    if (
      selectedGroup.value !== 'all' &&
      !availableGroups.value.some(
        group => String(group.id) === selectedGroup.value
      )
    )
      selectedGroup.value = 'all';
    resetDrafts(config);
    owned.value = config.owned_ids.join(', ');
    if (tab.value !== 'rules') {
      const path = tab.value === 'targets' ? '/targets' : '/decisions';
      const query = new URLSearchParams();
      if (tab.value === 'review') query.set('state', 'review');
      if (more && cursor.value) query.set('cursor', cursor.value);
      const data = await request(`${path}?${query}`);
      if (current !== epoch) return;
      rows.value = more ? [...rows.value, ...data.items] : data.items;
      cursor.value = data.next_cursor;
    }
    uncertain.value = false;
  } catch (e) {
    if (current !== epoch) return;
    error.value = errorMessage(e);
    denied.value = [403, 404].includes(e?.response?.status);
    if (denied.value) {
      rows.value = [];
      settings.value = null;
      dialog.value?.close();
    }
  } finally {
    if (current === epoch) loading.value = false;
  }
}

async function preview(row) {
  materialEpoch += 1;
  const current = materialEpoch;
  material.value = { id: row.id, loading: true };
  try {
    const data = await request(`/decisions/${row.id}/material`);
    if (current === materialEpoch) material.value = { id: row.id, data };
  } catch (failure) {
    if (current === materialEpoch)
      material.value = { id: row.id, error: errorMessage(failure) };
  }
}
async function save() {
  if (saving.value || uncertain.value) return;
  notice.value = '';
  wordErrors.value = {};
  ['stop_words'].forEach(key => {
    const message = wordListError(wordsValue(key), key);
    if (message) wordErrors.value[key] = message;
  });
  if (Object.keys(wordErrors.value).length) {
    error.value = 'Не сохранено. Исправьте строки, отмеченные под полями.';
    return;
  }
  const current = epoch;
  let ids;
  try {
    ids = allGroups.value
      ? parseOwnedIds(owned.value)
      : networkBaseline.value.owned_ids;
  } catch (e) {
    error.value = e.message;
    return;
  }
  saving.value = true;
  error.value = '';
  notice.value = '';
  try {
    const payload = {
      ...settings.value,
      ...(!allGroups.value ? networkBaseline.value : {}),
      settings: { groups: editedGroups.value },
      owned_ids: ids,
    };
    delete payload.notice_settings;
    delete payload.notice_available;
    const data = await request('/settings', 'patch', payload);
    if (current !== epoch) return;
    settings.value = data;
    resetDrafts(data);
    owned.value = data.owned_ids.join(', ');
    notice.value = allGroups.value
      ? `Сохранено для всех групп (${selectedIds.value.length}).`
      : 'Настройки выбранной группы сохранены.';
  } catch (e) {
    if (current === epoch) {
      error.value = `Сохранение не подтверждено. ${errorMessage(e)}`;
      uncertain.value = true;
    }
  } finally {
    if (current === epoch) saving.value = false;
  }
}
async function openAction(row, kind, verdict) {
  selected.value = { row, kind, verdict };
  note.value = '';
  networkRestriction.value = false;
  await nextTick();
  dialog.value?.open();
}
async function confirm() {
  if (
    !selected.value ||
    saving.value ||
    note.value.trim().length < 3 ||
    uncertain.value
  )
    return;
  const current = epoch;
  const action = selected.value;
  saving.value = true;
  error.value = '';
  try {
    const path =
      action.kind === 'restore'
        ? `/targets/${action.row.id}/restore`
        : `/decisions/${action.row.id}/review`;
    await request(path, 'post', {
      verdict: action.verdict,
      note: note.value.trim(),
      scope: networkRestriction.value ? 'network' : 'group',
    });
    if (current !== epoch) return;
    dialog.value?.close();
    selected.value = null;
    notice.value = 'Решение сохранено. Результаты действий появятся в журнале.';
    saving.value = false;
    noticeSaving.value = false;
    await load();
  } catch (e) {
    if (current === epoch) {
      error.value = errorMessage(e);
      uncertain.value = true;
      dialog.value?.close();
    }
  } finally {
    if (current === epoch) saving.value = false;
  }
}
watch(
  () => [route.params.accountId, tab.value],
  () => {
    epoch += 1;
    settings.value = null;
    selected.value = null;
    notice.value = '';
    saving.value = false;
    dialog.value?.close();
    load();
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  epoch += 1;
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading && !settings"
    loading-message="Загружаем модерацию…"
  >
    <template #header>
      <BaseSettingsHeader
        title="Модерация"
        description="Общие правила, решения и ограничения для вашей сети Telegram-групп."
      />
      <div class="overflow-x-auto max-w-full pb-1">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTab"
          @tab-changed="
            item => {
              if (!saving) tab = item.key;
            }
          "
        />
      </div>
    </template>
    <template #body>
      <div class="flex flex-col gap-6 min-w-0">
        <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
          {{ error }}
        </p>
        <p v-if="notice" role="status" class="text-sm text-n-teal-11">
          {{ notice }}
        </p>
        <div class="flex flex-wrap items-center justify-between gap-3">
          <span v-if="settings" class="text-sm text-n-slate-11">{{
            label(settings.mode)
          }}</span>
          <Button
            label="Обновить"
            icon="i-lucide-refresh-cw"
            variant="faded"
            color="slate"
            :disabled="loading || saving"
            @click="load()"
          />
        </div>
        <template v-if="settings && !denied">
          <Banner
            v-if="
              settings.authority_state && settings.authority_state !== 'new'
            "
            color="blue"
          >
            {{
              settings.authority_state === 'paused'
                ? 'Переключение приостановлено. Новые действия и списания заблокированы.'
                : 'Исполнение ещё не передано новой системе. Настройка правил сама по себе не включает модерацию.'
            }}
          </Banner>
          <Banner v-if="settings.mode !== 'enforce'" color="blue">
            {{
              settings.mode === 'trial'
                ? 'Пробный режим: решения записываются, сообщения не удаляются, запреты и списания лимитов не выполняются.'
                : 'Модерация выключена. Новые автоматические действия не выполняются.'
            }}
          </Banner>
          <template v-if="tab === 'rules'">
            <Banner color="slate">
              Правила группы действуют независимо от дополнительной ИИ-проверки.
              Если она включена, подтверждённый спам удаляется автоматически.
              Спорные решения остаются на проверке администратора.
            </Banner>
            <form class="flex flex-col gap-6" @submit.prevent="save">
              <div class="flex flex-col gap-2 max-w-xl">
                <label
                  for="moderation-mode"
                  class="text-sm font-medium text-n-slate-12"
                  >Режим работы всей сети</label
                >
                <select
                  id="moderation-mode"
                  v-model="settings.mode"
                  :disabled="saving || uncertain || !allGroups"
                  class="w-full text-sm"
                >
                  <option value="off">Выключена</option>
                  <option value="trial">Пробный режим</option>
                  <option
                    value="enforce"
                    :disabled="!settings.execution_available"
                  >
                    Рабочий режим
                  </option>
                </select>
                <p
                  v-if="!settings.execution_available"
                  class="text-sm text-n-slate-11"
                >
                  Рабочий режим станет доступен после проверки и разрешения
                  запуска.
                </p>
              </div>
              <div class="max-w-2xl flex flex-col gap-4">
                <label
                  for="moderation-group"
                  class="text-sm font-medium text-n-slate-12"
                  >Группа</label
                >
                <select
                  id="moderation-group"
                  v-model="selectedGroup"
                  :disabled="saving || uncertain || noticeSaving"
                  class="w-full text-sm"
                >
                  <option value="all">Все группы</option>
                  <option
                    v-for="group in availableGroups"
                    :key="group.id"
                    :value="String(group.id)"
                  >
                    {{ group.title }}
                  </option>
                </select>
                <p
                  v-if="
                    (
                      availableGroups.find(
                        group => String(group.id) === selectedGroup
                      )?.title || ''
                    ).length > 50
                  "
                  class="text-sm text-n-slate-11 break-words"
                >
                  {{
                    availableGroups.find(
                      group => String(group.id) === selectedGroup
                    )?.title
                  }}
                </p>
                <p
                  v-if="!availableGroups.length"
                  class="text-sm text-n-slate-11"
                >
                  Сначала подключите Telegram-группу в настройках.
                </p>
                <Banner v-if="allGroups" color="blue">
                  Изменённые поля применятся ко всем
                  {{ availableGroups.length }} группам. Остальные настройки
                  каждой группы сохранятся.
                </Banner>
                <p v-else class="text-sm text-n-slate-11">
                  Правила изменятся только у выбранной группы. Общие настройки
                  сети доступны при выборе «Все группы».
                </p>
                <p
                  v-if="
                    allGroups &&
                    groupRule &&
                    Object.values(groupRule).includes(undefined)
                  "
                  class="text-sm text-n-slate-11"
                >
                  Разные значения в группах отмечены отдельно. Измените поле,
                  чтобы установить общее значение.
                </p>
                <template v-if="groupRule">
                  <label
                    for="moderation-group-mode"
                    class="text-sm font-medium text-n-slate-12"
                    >{{
                      allGroups
                        ? 'Модерация всех групп'
                        : 'Модерация выбранной группы'
                    }}</label
                  >
                  <select
                    id="moderation-group-mode"
                    :value="groupRule.mode ?? ''"
                    @change="changeRule('mode', $event.target.value)"
                    :disabled="saving || uncertain"
                    class="w-full text-sm"
                  >
                    <option v-if="mixed('mode')" value="" disabled>
                      Разные значения
                    </option>
                    <option value="off">Выключена</option>
                    <option value="trial">Только наблюдение</option>
                    <option
                      value="enforce"
                      :disabled="!settings.execution_available"
                    >
                      Рабочий режим
                    </option>
                  </select>
                  <p class="text-sm text-n-slate-11">
                    Новые группы выключены по умолчанию. Общий пробный режим
                    запрещает любые наказания.
                  </p>
                  <label
                    v-for="rule in ruleRows"
                    :key="rule[0]"
                    class="flex items-start gap-3 py-3 border-b border-n-weak"
                  >
                    <input
                      :checked="groupRule[rule[0]] === true"
                      :indeterminate="mixed(rule[0])"
                      @change="changeRule(rule[0], $event.target.checked)"
                      type="checkbox"
                      :disabled="saving || uncertain"
                    />
                    <span class="min-w-0"
                      ><span class="block text-sm font-medium text-n-slate-12"
                        >{{ rule[1]
                        }}{{ mixed(rule[0]) ? ' — разные значения' : '' }}</span
                      ><span class="block text-sm text-n-slate-11">{{
                        rule[2]
                      }}</span></span
                    >
                  </label>
                  <fieldset
                    v-if="groupRule.media !== false"
                    class="flex flex-col gap-3"
                  >
                    <legend class="text-sm font-medium text-n-slate-12">
                      Какие вложения проверять без права публикации
                    </legend>
                    <label
                      v-for="media in mediaTypes"
                      :key="media[0]"
                      class="flex items-center gap-3 text-sm text-n-slate-12"
                    >
                      <input
                        :id="`moderation-media-${media[0]}`"
                        type="checkbox"
                        :checked="mediaChoice(media[0]) === true"
                        :indeterminate="mediaChoice(media[0]) === null"
                        :disabled="saving || uncertain"
                        @change="setMediaType(media[0], $event.target.checked)"
                      />
                      <span
                        >{{ media[1]
                        }}{{
                          mediaChoice(media[0]) === null
                            ? ' — разные значения'
                            : ''
                        }}</span
                      >
                    </label>
                    <p class="text-sm text-n-slate-11">
                      Выбранные типы считаются нарушением. Если ничего не
                      выбрано, вложения этим правилом не блокируются.
                    </p>
                  </fieldset>
                  <label
                    for="moderation-stop-words"
                    class="text-sm font-medium text-n-slate-12"
                    >Стоп-слова и стоп-фразы — по одной на строку</label
                  >
                  <textarea
                    id="moderation-stop-words"
                    :aria-invalid="Boolean(wordErrors.stop_words)"
                    aria-describedby="moderation-stop-words-help"
                    :placeholder="
                      mixed('stop_words') ? 'Разные значения в группах' : ''
                    "
                    :value="wordsValue('stop_words')"
                    :disabled="saving || uncertain"
                    class="h-32 min-h-32 max-h-none resize-y overflow-y-auto"
                    rows="5"
                    maxlength="8100"
                    @input="wordsChanged('stop_words', $event.target.value)"
                  />
                  <p
                    id="moderation-stop-words-help"
                    :role="wordErrors.stop_words ? 'alert' : undefined"
                    :class="
                      wordErrors.stop_words
                        ? 'text-sm text-n-ruby-11'
                        : 'text-sm text-n-slate-11'
                    "
                  >
                    {{
                      wordErrors.stop_words ||
                      'Совпадение запускает выбранное действие при нарушении. До 100 строк: слова и фразы от 2 до 80 символов или знаки, например $, € и ₽. Знаки ищутся буквально в любом месте текста.'
                    }}
                  </p>
                  <label
                    for="moderation-action"
                    class="text-sm font-medium text-n-slate-12"
                    >Действие при нарушении</label
                  >
                  <select
                    id="moderation-action"
                    :value="groupRule.action ?? ''"
                    @change="changeRule('action', $event.target.value)"
                    :disabled="saving || uncertain"
                    class="w-full text-sm"
                  >
                    <option v-if="mixed('action')" value="" disabled>
                      Разные значения
                    </option>
                    <option value="delete">Только удалить сообщение</option>
                    <option value="restrict">
                      Удалить; при повторных нарушениях запретить писать в этой
                      группе
                    </option>
                    <option value="network_restrict_permanent">
                      Удалить и бессрочно запретить писать во всех группах с
                      первого нарушения
                    </option>
                  </select>
                  <Banner
                    v-if="groupRule.action === 'network_restrict_permanent'"
                    color="amber"
                  >
                    После подтверждённого удаления автор не сможет писать во
                    всех управляемых группах до ручного снятия ограничения.
                    Участник остаётся в группах. Старые нарушения повторно не
                    обрабатываются.
                  </Banner>
                  <template v-if="groupRule.action === 'restrict'">
                    <Input
                      :value="groupRule.warning_limit ?? ''"
                      :placeholder="
                        mixed('warning_limit') ? 'Разные значения' : ''
                      "
                      @input="
                        changeRule(
                          'warning_limit',
                          $event.target.value === ''
                            ? ''
                            : Number($event.target.value)
                        )
                      "
                      type="number"
                      label="Число нарушений за сутки до ограничения (1–20)"
                      min="1"
                      max="20"
                      :disabled="saving || uncertain"
                    />
                    <label
                      for="moderation-duration"
                      class="text-sm font-medium text-n-slate-12"
                      >Срок запрета писать</label
                    >
                    <select
                      id="moderation-duration"
                      :value="groupRule.restriction_seconds ?? ''"
                      @change="
                        changeRule(
                          'restriction_seconds',
                          Number($event.target.value)
                        )
                      "
                      :disabled="saving || uncertain"
                      class="w-full text-sm"
                    >
                      <option
                        v-if="mixed('restriction_seconds')"
                        value=""
                        disabled
                      >
                        Разные значения
                      </option>
                      <option :value="3600">Один час</option>
                      <option :value="86400">Одни сутки</option>
                      <option :value="604800">Семь суток</option>
                    </select>
                  </template>
                </template>
              </div>
              <Banner color="amber">
                Область и срок запрета определяет выбранное действие. Наши
                аккаунты, администраторы и действующие оплаченные права
                защищены. Результат применения ограничения виден отдельно для
                каждой группы.
              </Banner>
              <div class="max-w-2xl flex flex-col gap-5">
                <Input
                  v-model="settings.topic"
                  label="Тематика сети"
                  placeholder="Например: услуги и обсуждения индустрии красоты"
                  :disabled="saving || uncertain || !allGroups"
                />
                <Input
                  v-model="owned"
                  label="Дополнительные Telegram ID наших аккаунтов"
                  placeholder="123456789, 987654321"
                  :disabled="saving || uncertain || !allGroups"
                  message="Авторизованные аккаунты защищены автоматически. Здесь можно добавить исключения вручную. Владелец и администраторы также освобождены от проверок."
                />
                <p class="text-sm text-n-slate-11">
                  Оплаченные объявления учитываются один раз. Сомнительные
                  материалы ждут проверки без списания и автоматического
                  удаления. Ответы платных участников проверяются и не расходуют
                  лимит. Разрешения наших аккаунтов также учитываются в разделе
                  «Размещения».
                </p>
              </div>
              <div
                class="flex flex-col items-start gap-3"
                data-testid="settings-save-feedback"
              >
                <p v-if="saving" role="status" class="text-sm text-n-slate-11">
                  Сохраняем настройки…
                </p>
                <p
                  v-else-if="error"
                  role="alert"
                  class="text-sm text-n-ruby-11"
                >
                  {{ error }}
                </p>
                <p
                  v-else-if="notice"
                  role="status"
                  class="text-sm text-n-teal-11"
                >
                  {{ notice }}
                </p>
                <Button
                  :label="
                    allGroups
                      ? 'Сохранить для всех групп'
                      : 'Сохранить настройки'
                  "
                  type="submit"
                  :is-loading="saving"
                  :disabled="loading || saving || uncertain"
                />
              </div>
            </form>
            <section
              class="mt-8 flex max-w-2xl flex-col gap-4"
              aria-labelledby="moderation-notice-title"
            >
              <h3
                id="moderation-notice-title"
                class="text-base font-medium text-n-slate-12"
              >
                Уведомление после удаления
              </h3>
              <p class="break-words text-sm font-medium text-n-slate-11">
                {{ noticeScope }}
              </p>
              <p class="text-sm text-n-slate-11">
                Для выбранной группы или всех групп. Работает только в рабочем
                режиме модерации. Отправляется только после подтверждённого
                удаления ботом. Изменения этого блока сохраняются отдельно и не
                меняют правила модерации.
              </p>
              <Banner v-if="!noticeAvailable" color-scheme="slate">
                Уведомления будут доступны после обновления исполнителя.
                Автоматическая отправка пока выключена.
              </Banner>
              <Banner v-if="noticeNeedsReview" color-scheme="ruby">
                Результат отправки или удаления уведомления не подтверждён.
                Новые уведомления в этой группе остановлены, чтобы не создавать
                дубли. Требуется проверка администратора. Модерация не
                отключается.
              </Banner>
              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <input
                  id="moderation-notice-enabled"
                  type="checkbox"
                  :checked="noticeRule.enabled === true"
                  :indeterminate="noticeRule.enabled === undefined"
                  :disabled="
                    noticeSaving ||
                    noticeUncertain ||
                    !noticeAvailable ||
                    !selectedIds.length
                  "
                  @change="changeNotice('enabled', $event.target.checked)"
                />
                Показывать уведомление после удаления<span
                  v-if="noticeRule.enabled === undefined"
                >
                  — разные значения</span
                >
              </label>
              <label
                for="moderation-notice-text"
                class="text-sm font-medium text-n-slate-12"
                >Текст уведомления</label
              >
              <textarea
                id="moderation-notice-text"
                :value="noticeRule.text ?? ''"
                rows="4"
                maxlength="1000"
                class="h-32 min-h-32 max-h-none resize-y overflow-y-auto"
                aria-describedby="moderation-notice-help"
                :placeholder="
                  noticeRule.text === undefined
                    ? 'Разные тексты в группах — ввод заменит текст во всех выбранных группах'
                    : ''
                "
                :disabled="
                  noticeSaving ||
                  noticeUncertain ||
                  !noticeAvailable ||
                  !selectedIds.length
                "
                :aria-invalid="Boolean(noticeError)"
                @input="changeNotice('text', $event.target.value)"
              />
              <p id="moderation-notice-help" class="text-sm text-n-slate-11">
                До 1000 символов. {name} заменяется именем автора. Сообщение
                отправляется в группу обычным текстом. Одновременно в группе —
                не более одного уведомления. Пока оно показано, остальные
                пропускаются без очереди. Через 10 секунд бот удаляет
                уведомление; при недоступности Telegram удаление может
                задержаться.
              </p>
              <div
                v-if="noticeRule.text"
                class="flex flex-col gap-1 rounded-lg border border-n-weak p-4"
              >
                <p class="text-sm font-medium text-n-slate-12">
                  Предпросмотр · пример имени: Анна
                </p>
                <p
                  class="whitespace-pre-wrap break-words text-sm text-n-slate-11"
                >
                  {{ noticeRule.text.replaceAll('{name}', 'Анна') }}
                </p>
              </div>
              <p
                v-if="noticeSaving"
                role="status"
                class="text-sm text-n-slate-11"
              >
                Сохраняем уведомление…
              </p>
              <p
                v-else-if="noticeError"
                role="alert"
                class="text-sm text-n-ruby-11"
              >
                {{ noticeError }}
              </p>
              <p
                v-else-if="noticeSaved"
                role="status"
                class="text-sm text-n-teal-11"
              >
                {{ noticeSaved }}
              </p>
              <Button
                label="Сохранить уведомление"
                class="self-start"
                type="button"
                :is-loading="noticeSaving"
                :disabled="
                  noticeSaving ||
                  noticeUncertain ||
                  !noticeAvailable ||
                  !selectedIds.length ||
                  !Object.keys(noticeEdits[selectedGroup] || {}).length
                "
                @click="saveNotice"
              />
            </section>
          </template>
          <template v-else>
            <p v-if="loading" role="status" class="text-sm text-n-slate-11">
              Загружаем записи…
            </p>
            <div
              v-else-if="!rows.length && !error"
              class="py-16 text-center flex flex-col gap-2"
            >
              <p class="text-base font-medium text-n-slate-12">
                {{ emptyTitle }}
              </p>
              <p class="text-sm text-n-slate-11">
                Здесь появятся результаты обработки сообщений после включения
                модерации.
              </p>
            </div>
            <div v-else class="divide-y divide-n-weak border-y border-n-weak">
              <article
                v-for="row in rows"
                :key="row.id"
                class="py-5 flex flex-col gap-3 min-w-0"
              >
                <div class="flex flex-wrap justify-between gap-2">
                  <span
                    class="text-sm font-medium text-n-slate-12 break-words"
                    >{{ row.group_title }}</span
                  ><span class="text-sm text-n-slate-11">{{
                    label(row.state)
                  }}</span>
                </div>
                <p
                  v-if="row.created_at || row.updated_at"
                  class="text-xs text-n-slate-11 mb-0"
                >
                  {{ formatDate(row.created_at || row.updated_at) }}
                </p>
                <p
                  v-if="row.text"
                  class="text-sm text-n-slate-12 whitespace-pre-wrap break-words mb-0"
                >
                  {{ row.text }}
                </p>
                <p class="text-xs text-n-slate-11 mb-0">
                  Telegram ID: {{ row.telegram_user_id || 'Не установлен'
                  }}<template v-if="row.reason">
                    · {{ label(row.reason) }} · {{ label(row.mode) }}
                  </template>
                </p>
                <p
                  v-for="(action, index) in row.actions || []"
                  :key="index"
                  class="text-xs text-n-slate-11 mb-0"
                >
                  {{ label(action.kind) }}: {{ label(action.state) }}
                  <template
                    v-if="
                      action.result_code && action.result_code !== action.state
                    "
                  >
                    ·
                    {{
                      action.result_code === 'transport_unconfirmed'
                        ? 'Нет подтверждения от Telegram'
                        : action.result_code
                    }}
                  </template>
                </p>
                <div
                  v-if="tab !== 'targets'"
                  class="flex flex-wrap items-center gap-3"
                >
                  <Button
                    label="Просмотреть объявление"
                    variant="faded"
                    color="slate"
                    @click="preview(row)"
                  />
                  <a
                    v-if="row.telegram_url"
                    :href="row.telegram_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-sm text-n-blue-11 underline"
                    >Открыть в Telegram</a
                  >
                </div>
                <section
                  v-if="material?.id === row.id"
                  aria-label="Материалы объявления"
                  class="flex flex-col gap-3"
                >
                  <p
                    v-if="material.loading"
                    role="status"
                    class="text-sm text-n-slate-11"
                  >
                    Загружаем объявление…
                  </p>
                  <template v-else-if="material.error">
                    <p role="alert" class="text-sm text-n-ruby-9">
                      {{ material.error }}
                    </p>
                    <Button
                      label="Повторить загрузку"
                      variant="faded"
                      color="slate"
                      @click="preview(row)"
                    />
                  </template>
                  <template v-else-if="material.data">
                    <p
                      v-if="
                        material.data.media_pending ||
                        material.data.media_unavailable
                      "
                      class="text-sm text-n-slate-11"
                    >
                      Часть вложений ещё не загружена или недоступна. Проверьте
                      весь пост в Telegram перед решением.
                    </p>
                    <ReviewMedia
                      :attachments="material.data.attachments || []"
                    />
                    <p
                      class="text-sm text-n-slate-12 whitespace-pre-wrap break-words"
                    >
                      {{ material.data.text }}
                    </p>
                    <a
                      :href="material.data.conversation_url"
                      class="text-sm text-n-blue-11 underline"
                      >Открыть в диалогах</a
                    >
                  </template>
                </section>
                <p
                  v-if="row.placement_state === 'reserved'"
                  class="text-sm text-n-slate-11"
                >
                  Одно размещение зарезервировано. Оплата ещё не списана.
                </p>
                <div v-if="row.state === 'review'" class="flex flex-wrap gap-2">
                  <Button
                    :label="
                      row.placement_state === 'reserved'
                        ? 'Подтвердить объявление — 1 размещение'
                        : 'Разрешить'
                    "
                    variant="faded"
                    color="slate"
                    :disabled="saving || uncertain"
                    @click="openAction(row, 'review', 'allow')"
                  />
                  <Button
                    v-if="row.placement_state === 'reserved'"
                    label="Обычное сообщение — без списания"
                    variant="faded"
                    color="slate"
                    :disabled="saving || uncertain"
                    @click="openAction(row, 'review', 'ordinary')"
                  />
                  <Button
                    label="Подтвердить нарушение"
                    variant="faded"
                    color="ruby"
                    :disabled="saving || uncertain || !row.telegram_user_id"
                    @click="openAction(row, 'review', 'violation')"
                  />
                </div>
                <p
                  v-if="row.review_note"
                  class="text-sm text-n-slate-11 break-words mb-0"
                >
                  Причина решения: {{ row.review_note }}
                </p>
                <div v-if="tab === 'targets'">
                  <Button
                    label="Разрешить писать в этой группе"
                    variant="faded"
                    color="slate"
                    :disabled="
                      row.state !== 'succeeded' ||
                      row.exception ||
                      saving ||
                      uncertain ||
                      !settings.execution_available
                    "
                    @click="openAction(row, 'restore')"
                  />
                </div>
              </article>
            </div>
            <div v-if="cursor">
              <Button
                label="Показать ещё"
                variant="faded"
                color="slate"
                :disabled="loading || saving"
                @click="load(true)"
              />
            </div>
          </template>
        </template>
      </div>
    </template>
  </SettingsLayout>
  <Dialog
    ref="dialog"
    :title="title"
    :description="dialogDescription"
    cancel-button-label="Отмена"
    confirm-button-label="Подтвердить"
    :is-loading="saving"
    :disable-confirm-button="
      note.trim().length < 3 || note.length > 500 || uncertain
    "
    @confirm="confirm"
  >
    <p v-if="selected" class="text-sm text-n-slate-11 break-words">
      {{ selected.row.group_title }}
    </p>
    <label
      v-if="
        selected?.verdict === 'violation' && selected?.row.mode === 'enforce'
      "
      class="flex items-start gap-3 mb-4 text-sm text-n-slate-12"
    >
      <input v-model="networkRestriction" type="checkbox" :disabled="saving" />
      Подтверждаю мошенничество или систематический спам: ограничить во всех
      включённых группах
    </label>
    <Input
      v-model="note"
      label="Причина решения"
      placeholder="Укажите причину, от 3 до 500 символов"
      :disabled="saving"
      autofocus
    />
  </Dialog>
</template>
